<script setup>
/* global axios */
import { ref, computed, watch, onMounted, onActivated } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import OverviewTab from 'dashboard/components-next/Campaigns/Reports/OverviewTab.vue';
import DeliveryTab from 'dashboard/components-next/Campaigns/Reports/DeliveryTab.vue';
import EngagementTab from 'dashboard/components-next/Campaigns/Reports/EngagementTab.vue';

const route = useRoute();
const router = useRouter();
const { accountId, accountScopedRoute } = useAccount();
const { localeTag } = useDateLocale();
const { t } = useI18n();

const loading = ref(true);
const reportLoading = ref(false);
const campaign = ref(null);
const deliveries = ref([]);
const report = ref(null);
const currentPage = ref(1);
const totalCount = ref(0);
const perPage = ref(25);
const activeTab = ref('overview');

const campaignId = computed(() => route.params.campaignId);
const showPagination = computed(() => totalCount.value > perPage.value);

const TABS = [
  { key: 'overview', name: t('CAMPAIGN.DELIVERIES.TABS.OVERVIEW') },
  { key: 'delivery', name: t('CAMPAIGN.DELIVERIES.TABS.DELIVERY') },
  { key: 'engagement', name: t('CAMPAIGN.DELIVERIES.TABS.ENGAGEMENT') },
  { key: 'list', name: t('CAMPAIGN.DELIVERIES.TABS.LIST') },
];

const selectedTabIndex = computed(() =>
  Math.max(
    TABS.findIndex(tab => tab.key === activeTab.value),
    0
  )
);

function onTabChange(index) {
  activeTab.value = TABS[index].key;
  if (activeTab.value !== 'list' && !report.value) {
    loadReport(); // eslint-disable-line no-use-before-define
  }
}

const STATUS_CONFIG = {
  pending: {
    label: t('CAMPAIGN.DELIVERIES.STATUS.PENDING'),
    icon: 'i-lucide-clock',
    cls: 'bg-n-slate-3 text-n-slate-11',
  },
  sent: {
    label: t('CAMPAIGN.DELIVERIES.STATUS.SENT'),
    icon: 'i-lucide-check',
    cls: 'bg-n-blue-3 text-n-blue-11',
  },
  delivered: {
    label: t('CAMPAIGN.DELIVERIES.STATUS.DELIVERED'),
    icon: 'i-lucide-check-check',
    cls: 'bg-n-teal-3 text-n-teal-11',
  },
  read: {
    label: t('CAMPAIGN.DELIVERIES.STATUS.READ'),
    icon: 'i-lucide-eye',
    cls: 'bg-n-green-3 text-n-green-11',
  },
  failed: {
    label: t('CAMPAIGN.DELIVERIES.STATUS.FAILED'),
    icon: 'i-lucide-x-circle',
    cls: 'bg-n-ruby-3 text-n-ruby-11',
  },
};

const TABLE_HEADERS = [
  t('CAMPAIGN.DELIVERIES.HEADERS.CONTACT'),
  t('CAMPAIGN.DELIVERIES.HEADERS.STATUS'),
  t('CAMPAIGN.DELIVERIES.HEADERS.SENT_AT'),
  t('CAMPAIGN.DELIVERIES.HEADERS.DELIVERED_AT'),
  t('CAMPAIGN.DELIVERIES.HEADERS.READ_AT'),
];

async function loadDeliveries() {
  loading.value = true;
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/escaleshop/campaigns/${campaignId.value}/deliveries`,
      { params: { page: currentPage.value } }
    );
    campaign.value = data.campaign;
    deliveries.value = data.deliveries;
    totalCount.value = data.meta?.total_count ?? 0;
    perPage.value = data.meta?.per_page ?? 25;
  } catch (_e) {
    // swallow — UI shows empty state
  } finally {
    loading.value = false;
  }
}

async function loadReport() {
  reportLoading.value = true;
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/escaleshop/campaigns/${campaignId.value}/deliveries/report`
    );
    report.value = data.report;
  } catch (_e) {
    // swallow — UI shows empty state
  } finally {
    reportLoading.value = false;
  }
}

function load() {
  report.value = null;
  deliveries.value = [];
  loadDeliveries();
  loadReport();
}

onMounted(load);
onActivated(load);

watch(campaignId, () => {
  currentPage.value = 1;
  report.value = null;
  load();
});

watch(currentPage, loadDeliveries);

function formatDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString(localeTag.value);
}
</script>

<template>
  <div class="flex flex-col gap-6 py-8 max-w-7xl mx-auto w-full px-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <button
          class="flex items-center justify-center size-8 rounded-lg hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click="router.push(accountScopedRoute('campaigns_whatsapp_index'))"
        >
          <span class="i-lucide-arrow-left size-4" />
        </button>
        <div>
          <h1 class="text-heading-1 text-n-slate-12">
            {{ campaign?.title || '...' }}
          </h1>
          <p class="text-sm text-n-slate-11 mt-0.5">
            {{ t('CAMPAIGN.DELIVERIES.REPORT_TITLE') }}
          </p>
        </div>
      </div>
      <button
        class="flex items-center gap-1.5 text-sm rounded-lg px-2 py-1.5 hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 transition-colors disabled:opacity-50"
        :disabled="loading || reportLoading"
        @click="load"
      >
        <span
          class="i-lucide-refresh-cw size-4"
          :class="{ 'animate-spin': loading || reportLoading }"
        />
        {{ t('CAMPAIGN.DELIVERIES.REFRESH') }}
      </button>
    </div>

    <woot-tabs
      class="[&_ul]:p-0 top-px relative"
      :index="selectedTabIndex"
      border
      @change="onTabChange"
    >
      <woot-tabs-item
        v-for="(tab, index) in TABS"
        :key="tab.key"
        :index="index"
        :name="tab.name"
        :show-badge="false"
        is-compact
      />
    </woot-tabs>

    <!-- Tabbed content -->
    <div v-if="activeTab !== 'list'">
      <OverviewTab
        v-if="activeTab === 'overview'"
        :report="report"
        :loading="reportLoading && !report"
      />
      <DeliveryTab
        v-else-if="activeTab === 'delivery'"
        :report="report"
        :loading="reportLoading && !report"
      />
      <EngagementTab
        v-else-if="activeTab === 'engagement'"
        :report="report"
        :loading="reportLoading && !report"
      />
    </div>

    <!-- List tab: paginated deliveries -->
    <div
      v-else
      class="flex flex-col gap-3 border rounded-lg border-n-slate-4 bg-n-solid-1 p-4"
    >
      <div class="flex items-center justify-between">
        <span class="text-sm text-n-slate-11">
          {{
            t(
              'CAMPAIGN.DELIVERIES.SENDS_COUNT',
              { count: totalCount.toLocaleString(localeTag.value) },
              totalCount
            )
          }}
        </span>
      </div>

      <BaseTable
        :headers="TABLE_HEADERS"
        :items="deliveries"
        :loading="loading"
        :no-data-message="t('CAMPAIGN.DELIVERIES.NO_RECORDS')"
      >
        <template #skeleton-row>
          <td class="py-4 ltr:pr-4 rtl:pl-4">
            <div class="flex items-center gap-2.5">
              <div class="size-7 rounded-full bg-n-alpha-2 shrink-0" />
              <div class="flex flex-col gap-1.5">
                <div class="h-3.5 w-32 rounded bg-n-alpha-2" />
                <div class="h-3 w-24 rounded bg-n-alpha-2" />
              </div>
            </div>
          </td>
          <td class="py-4 ltr:pr-4 rtl:pl-4">
            <div class="h-5 w-20 rounded-full bg-n-alpha-2" />
          </td>
          <td v-for="i in 3" :key="i" class="py-4 ltr:pr-4 rtl:pl-4">
            <div class="h-4 w-24 rounded bg-n-alpha-2" />
          </td>
        </template>

        <template #row="{ items }">
          <BaseTableRow v-for="d in items" :key="d.id" :item="d">
            <template #default>
              <BaseTableCell>
                <div class="flex items-center gap-2.5">
                  <img
                    v-if="d.contact.thumbnail"
                    :src="d.contact.thumbnail"
                    :alt="d.contact.name"
                    class="size-7 rounded-full object-cover shrink-0"
                  />
                  <span
                    v-else
                    class="size-7 rounded-full bg-n-alpha-2 flex items-center justify-center shrink-0"
                  >
                    <span class="i-lucide-user size-3.5 text-n-slate-9" />
                  </span>
                  <div class="flex flex-col min-w-0">
                    <span
                      class="font-medium text-n-slate-12 truncate max-w-[180px]"
                    >
                      {{ d.contact.name || '—' }}
                    </span>
                    <span
                      class="text-xs text-n-slate-10 font-mono truncate max-w-[180px]"
                    >
                      {{ d.contact.phone_number || '—' }}
                    </span>
                  </div>
                </div>
              </BaseTableCell>
              <BaseTableCell>
                <span
                  class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                  :class="STATUS_CONFIG[d.status]?.cls"
                >
                  <span :class="STATUS_CONFIG[d.status]?.icon" class="size-3" />
                  {{ STATUS_CONFIG[d.status]?.label }}
                </span>
                <p
                  v-if="d.error_message"
                  class="text-xs text-n-ruby-11 mt-1 max-w-xs truncate"
                >
                  {{ d.error_message }}
                </p>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-n-slate-11">{{ formatDate(d.sent_at) }}</span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-n-slate-11">{{
                  formatDate(d.delivered_at)
                }}</span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-n-slate-11">{{ formatDate(d.read_at) }}</span>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>

      <PaginationFooter
        v-if="showPagination"
        :current-page="currentPage"
        :total-items="totalCount"
        :items-per-page="perPage"
        @update:current-page="p => (currentPage = p)"
      />
    </div>
  </div>
</template>
